import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "@/shared/lib/api";
import type { Advisor } from "@/shared/types";

interface AdvisorState {
  advisor: Advisor | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    name: string;
    company?: string;
    phone?: string;
    // Role distinction fields
    roleType?: "doradca" | "ekspert" | "oba";
    rpkNumber?: string;
    rpkDivision?: "I" | "II" | "I_II";
    compensationModel?: "client" | "bank" | "mixed";
    nip?: string;
  }) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: {
    name?: string;
    company?: string;
    phone?: string;
    // Role distinction fields
    roleType?: "doradca" | "ekspert" | "oba";
    rpkNumber?: string;
    rpkDivision?: "I" | "II" | "I_II";
    compensationModel?: "client" | "bank" | "mixed";
    nip?: string;
  }) => Promise<void>;
  clearError: () => void;
  updateCredits: (credits: number) => void;
}

export const useAdvisorStore = create<AdvisorState>()(
  persist(
    (set, get) => ({
      advisor: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          api.setTokenContext("advisor");
          const response = await api.login(email, password);
          api.setToken(response.token, "advisor");
          set({
            advisor: response.advisor as Advisor,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err) {
          const error = err as Error;
          set({
            error: error.message || "Błąd logowania",
            isLoading: false,
          });
          throw err;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.register(data);
          set({ isLoading: false });
          return {
            success: response.success,
            message: response.message,
          };
        } catch (err) {
          const error = err as Error;
          set({
            error: error.message || "Błąd rejestracji",
            isLoading: false,
          });
          throw err;
        }
      },

      logout: () => {
        api.setToken(null, "advisor");
        set({
          advisor: null,
          isAuthenticated: false,
          error: null,
        });
      },

      fetchProfile: async () => {
        if (!get().isAuthenticated) return;

        set({ isLoading: true });
        try {
          const response = await api.getProfile();
          set({
            advisor: response.advisor as Advisor,
            isLoading: false,
          });
        } catch (err) {
          const error = err as Error;
          // If 401, logout
          if ((error as Error & { status?: number }).status === 401) {
            get().logout();
          }
          set({
            error: error.message,
            isLoading: false,
          });
        }
      },

      updateProfile: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.updateProfile(data);
          set({
            advisor: response.advisor as Advisor,
            isLoading: false,
          });
        } catch (err) {
          const error = err as Error;
          set({
            error: error.message || "Błąd aktualizacji profilu",
            isLoading: false,
          });
          throw err;
        }
      },

      clearError: () => set({ error: null }),

      updateCredits: (credits: number) => {
        const advisor = get().advisor;
        if (advisor) {
          set({ advisor: { ...advisor, credits } });
        }
      },
    }),
    {
      name: "advisor-storage",
      partialize: (state) => ({
        advisor: state.advisor,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Restore advisor token from localStorage when store is rehydrated
        if (state?.isAuthenticated) {
          api.setTokenContext("advisor");
          const token = localStorage.getItem("advisor_auth_token");
          if (token) {
            api.setToken(token, "advisor");
          } else {
            // No token but authenticated - reset state
            state.advisor = null;
            state.isAuthenticated = false;
          }
        }
      },
    }
  )
);
